// ApiKeysTab — extracted from the pre-tabs DevelopersTool/index.jsx without
// any behavior change. The shell in index.jsx now renders this component as
// one of two tabs (the other being WebhooksTab).
import React, {Component} from 'react';
import {ContentCopy, Add, VpnKey} from '@mui/icons-material';
import {
  CircularProgress, IconButton, Tooltip, Button, Dialog, DialogTitle,
  DialogContent, DialogActions, TextField, Chip, Alert, Snackbar
} from '@mui/material';

import Api from '../../api';
import Table from '../../components/Table';
import {UI_API_URL_PATH_NEW} from '../../lib/constant';
import {getOidcAccessToken, decodeJwtPayload} from '../../auth/tokenUtils';

// Org admins/owners (and platform admins) can self-manage their own org's
// API keys via the /api/v1/me/apikeys surface — generate a 2nd key for
// zero-downtime rotation, then revoke the old one. The backend `me:` authz
// allows any authenticated org member; the UI surfaces the actions for the
// org-management roles. Lower roles (sender/analytics) just list.
const MANAGE_API_KEY_ROLES = ['admin', 'org_admin', 'org_owner'];
const canManageApiKeys = () => {
  const claims = decodeJwtPayload(getOidcAccessToken()) || {};
  const combirdRoles = claims.combird_roles || {};
  for (const proj of Object.values(combirdRoles)) {
    const roles = proj?.roles || [];
    if (roles.some((r) => MANAGE_API_KEY_ROLES.includes(r))) return true;
  }
  return false;
};

// Max active API keys per channel — mirrors the backend
// APIKEY_MAX_KEYS_PER_PROJECT (default 2): generate a 2nd key for
// zero-downtime rotation, then revoke the older one.
const MAX_KEYS_PER_CHANNEL = 2;

// Channels with a provisioned machine user. Others 403 "API access not
// provisioned" on create. The channel picker was removed from the create
// dialog (SMS is the only live channel; newKeyProject stays 'sms') — this
// list still drives the per-channel cap that disables the Create button.
// If a second channel goes live, re-add a picker.
const PROVISIONED_CHANNELS = [{value: 'sms', label: 'SMS'}];

export default class ApiKeysTab extends Component {
  state = {
    keys: [],
    loading: true,
    createDialogOpen: false,
    creating: false,
    newKeyName: '',
    newKeyProject: 'sms',
    newKeyDescription: '',
    createdKey: null,
    snackbar: {open: false, message: '', severity: 'success'},
    canManage: canManageApiKeys()
  }

  componentDidMount() {
    this.getAPIKeys();
  }

  getAPIKeys = () => {
    this.setState({loading: true});
    // Customer self-service surface (org-from-JWT, me: authz — works for org
    // owners without a product-project role, unlike /api/v1/apikeys).
    Api.get(`${UI_API_URL_PATH_NEW}/api/v1/me/apikeys`)
      .then(response => {
        const keys = response?.data?.data?.list || [];
        this.setState({keys, loading: false});
      })
      .catch(err => {
        console.error('Failed to fetch API keys:', err);
        this.setState({keys: [], loading: false});
      });
  }

  createAPIKey = () => {
    const {newKeyName, newKeyProject, newKeyDescription} = this.state;
    if (!newKeyName.trim()) return;

    this.setState({creating: true});
    Api.post(`${UI_API_URL_PATH_NEW}/api/v1/me/apikeys`, {
      key_name: newKeyName.trim(),
      project: newKeyProject,
      description: newKeyDescription.trim()
    })
      .then(response => {
        const data = response?.data?.data;
        this.setState({
          creating: false,
          createDialogOpen: false,
          createdKey: data,
          newKeyName: '',
          newKeyDescription: '',
          snackbar: {open: true, message: 'API key created successfully', severity: 'success'}
        });
        this.getAPIKeys();
      })
      .catch(err => {
        const msg = err?.response?.data?.message || 'Failed to create API key';
        this.setState({
          creating: false,
          snackbar: {open: true, message: msg, severity: 'error'}
        });
      });
  }

  revokeAPIKey = (id, keyName) => {
    // Revoking the last active key for a channel kills every integration
    // still authenticating with it — warn loudly, but don't block: if the
    // key is compromised the customer must be able to revoke it immediately.
    const key = this.state.keys.find((k) => k.id === id);
    const isLastActiveKey = key && this.activeCountForChannel(key.project) === 1;
    const message = isLastActiveKey
      ? `"${keyName}" is your ONLY active API key. Revoking it will break your integration — all API requests using this key will start failing immediately, and this cannot be undone.\n\nFor zero-downtime rotation, create a new key first, then revoke this one.\n\nRevoke anyway?`
      : `Revoke API key "${keyName}"? This cannot be undone.`;
    if (!window.confirm(message)) return;

    Api.post(`${UI_API_URL_PATH_NEW}/api/v1/me/apikeys/${id}/revoke`, {reason: 'Revoked from portal'})
      .then(() => {
        this.setState({
          snackbar: {open: true, message: 'API key revoked', severity: 'success'}
        });
        this.getAPIKeys();
      })
      .catch(err => {
        const msg = err?.response?.data?.message || 'Failed to revoke API key';
        this.setState({
          snackbar: {open: true, message: msg, severity: 'error'}
        });
      });
  }

  // Count active keys for a channel — drives the per-channel 2-key cap UI
  // (mirrors the backend, which only counts is_active=true toward the limit).
  activeCountForChannel = (channel) =>
    this.state.keys.filter((k) => (k.isActive ?? k.is_active) && k.project === channel).length;

  copyToClipboard = (e, text) => {
    e.stopPropagation();
    navigator.clipboard.writeText(text).then(() => {
      this.setState({snackbar: {open: true, message: 'Copied to clipboard', severity: 'success'}});
    }).catch(() => {
      const textField = document.createElement('textarea');
      textField.innerText = text;
      document.body.appendChild(textField);
      textField.select();
      document.execCommand('copy');
      textField.remove();
    });
  }

  render() {
    const {keys, loading, createDialogOpen, creating, newKeyName, newKeyProject, newKeyDescription, createdKey, snackbar, canManage} = this.state;

    // Per-channel 2-key cap (backend-enforced; mirrored here for the UI).
    const channelCount = this.activeCountForChannel(newKeyProject);
    const channelAtCap = channelCount >= MAX_KEYS_PER_CHANNEL;
    const allChannelsFull = PROVISIONED_CHANNELS.every(
      (c) => this.activeCountForChannel(c.value) >= MAX_KEYS_PER_CHANNEL
    );

    return (
      <div>
        <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16}}>
          <h4 style={{color: 'black', fontWeight: 300, fontSize: 18, margin: 0}}>
            API Keys for your account. Use these keys to authenticate API requests.
          </h4>
          {canManage && (
            <Tooltip title={allChannelsFull ? `Limit reached — ${MAX_KEYS_PER_CHANNEL} keys per channel. Revoke one to add another.` : ''}>
              <span>
                <Button
                  variant="contained"
                  startIcon={<Add />}
                  onClick={() => this.setState({createDialogOpen: true})}
                  size="small"
                  disabled={allChannelsFull}
                >
                  Create API Key
                </Button>
              </span>
            </Tooltip>
          )}
        </div>

        {createdKey && (
          <Alert
            severity="success"
            onClose={() => this.setState({createdKey: null})}
            style={{marginBottom: 16}}
            action={
              <Tooltip title="Copy Key">
                <IconButton size="small" onClick={(e) => this.copyToClipboard(e, createdKey.plaintextKey || createdKey.plaintext_key)}>
                  <ContentCopy fontSize="small" />
                </IconButton>
              </Tooltip>
            }
          >
            <strong>Save this key now — it will not be shown again:</strong>
            <br />
            <code style={{fontSize: 12, wordBreak: 'break-all'}}>
              {createdKey.plaintextKey || createdKey.plaintext_key}
            </code>
          </Alert>
        )}

        {loading ? <CircularProgress /> : (
          <div>
            {keys.length > 0 ? (
              <Table
                column={[
                  {name: 'Name', value: 'keyName', cell: val => val || '-'},
                  {name: 'Status', value: 'isActive', cell: val => val ? <Chip label="Active" size="small" color="success" /> : <Chip label="Revoked" size="small" color="error" />},
                  {name: 'Created', value: 'createdAt', cell: val => val ? new Date(val).toLocaleDateString() : '-'},
                  {name: 'API Key', value: 'token', cell: (val) => val ? (
                    <div style={{display: 'flex', alignItems: 'center'}}>
                      <code style={{fontSize: 11, maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'inline-block'}}>{val}</code>
                      <Tooltip title="Copy API Key">
                        <IconButton size="small" onClick={(e) => this.copyToClipboard(e, val)}>
                          <ContentCopy fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </div>
                  ) : '-'},
                  {name: 'Actions', value: 'id', cell: (val, row) => (canManage && row.isActive) ? (
                    <Button size="small" color="error" onClick={() => this.revokeAPIKey(val, row.keyName)}>Revoke</Button>
                  ) : null}
                ]}
                data={keys}
              />
            ) : (
              <div style={{textAlign: 'center', padding: 40}}>
                <VpnKey style={{fontSize: 48, color: '#ccc'}} />
                <p>{canManage ? 'No API keys found. Create one to get started.' : 'No API keys found. Keys are provisioned during org onboarding — contact platform support if you need one.'}</p>
              </div>
            )}
          </div>
        )}

        <Dialog open={createDialogOpen} onClose={() => this.setState({createDialogOpen: false})} maxWidth="sm" fullWidth>
          <DialogTitle>Create API Key</DialogTitle>
          <DialogContent>
            <TextField
              autoFocus
              margin="dense"
              label="Key Name"
              fullWidth
              variant="outlined"
              value={newKeyName}
              onChange={(e) => this.setState({newKeyName: e.target.value})}
              placeholder="e.g. my-backend-service"
              style={{marginBottom: 16}}
            />
            <TextField
              margin="dense"
              label="Description (optional)"
              fullWidth
              variant="outlined"
              value={newKeyDescription}
              onChange={(e) => this.setState({newKeyDescription: e.target.value})}
              placeholder="What this key will be used for"
              multiline
              rows={2}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => this.setState({createDialogOpen: false})}>Cancel</Button>
            <Button onClick={this.createAPIKey} variant="contained" disabled={creating || !newKeyName.trim() || channelAtCap}>
              {creating ? <CircularProgress size={20} /> : 'Create'}
            </Button>
          </DialogActions>
        </Dialog>

        <Snackbar
          open={snackbar.open}
          autoHideDuration={4000}
          onClose={() => this.setState({snackbar: {...snackbar, open: false}})}
          anchorOrigin={{vertical: 'bottom', horizontal: 'center'}}
        >
          <Alert severity={snackbar.severity} onClose={() => this.setState({snackbar: {...snackbar, open: false}})}>
            {snackbar.message}
          </Alert>
        </Snackbar>
      </div>
    );
  }
}
